/**
 * One practitioner's availability, wherever it is shown (the person's staff
 * page, or Clinic Setup › Hours with its picker): the weekly working hours —
 * six presets that REPLACE the schedule, the manual add form, and a per-range
 * Change / Remove — and time off with the affected-bookings notice. The
 * backend contract is one range per write with no edit endpoint; a change is
 * therefore two writes, ordered and restored so a refusal never costs the
 * person their existing schedule (see availabilityPresets.editPlan).
 */

import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useIntl } from 'react-intl';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import a from '../access/access.module.css';
import { Button, Chip, Notice, Skeleton, TextField } from '@/ui/kit';
import { ConfirmDialog, FieldGrid, FormCard, SelectField, Span2, Toggle } from '@/ui/formkit';
import { DateField } from '@/ui/DateField';
import {
  addTimeOff,
  addWorkingHours,
  deleteTimeOff,
  deleteWorkingHours,
  fetchPractitionerAvailability,
  modesOf,
  type DeliveryMode,
  type WorkingHoursView,
} from '@/core/clinic/setupApi';
import { ModeSetField, modesLabel } from './ModeSetField';
import { ApiError } from '@/core/api/errors';
import { useAuthz } from '@/core/authz/AuthzProvider';
import { useLocale } from '@/core/i18n/LocaleProvider';
import { formatDate, formatTime, localizeDigits } from '@/core/calendar/format';
import { useClinicSettings } from '../patients/useClinicSettings';
import { useBookingsWindow } from '../scheduling/useSchedulingData';
import { instantFromLocal, localDateISO, parseDateISO } from '../scheduling/calendarMath';
import {
  DAY_IDS,
  editPlan,
  hhmmToMinute,
  minuteToHHMM,
  planPreset,
  presetModes,
  PRESET_DAYS,
  PRESET_END_MINUTE,
  PRESET_LOCATIONS,
  PRESET_PATTERNS,
  PRESET_START_MINUTE,
  type PresetLocation,
  type PresetPattern,
  type RangeInput,
} from './availabilityPresets';

/* ------------------------------- shared bits -------------------------------- */

export function usePractitionerAvailability(uid: string) {
  const authz = useAuthz();
  return useQuery({
    queryKey: ['availability', authz.clinicId, uid],
    queryFn: () => fetchPractitionerAvailability(uid),
    enabled: uid !== '',
    retry: false,
  });
}

export function useDayNames() {
  const { locale } = useLocale();
  const dayName = (iso: number) =>
    new Intl.DateTimeFormat(locale === 'fa' ? 'fa-IR' : 'en-US', { weekday: 'long' }).format(
      // 2026-08-17 is a Monday; ISO 1 = Monday … 7 = Sunday.
      new Date(Date.UTC(2026, 7, 16 + iso, 12)),
    );
  return { dayName, dayNames: DAY_IDS.map(dayName) };
}

const errorText = (err: unknown, fallback: string, overlap?: string): string =>
  err instanceof ApiError && err.status === 409 && overlap
    ? overlap
    : err instanceof ApiError && err.serverMessage
      ? err.serverMessage
      : fallback;

/** The availability read 404s for a member without practitioner details — a fact, not a failure. */
export const isNoPractitioner = (err: unknown): boolean =>
  err instanceof ApiError && err.status === 404;

/* ------------------------------ working hours ------------------------------- */

export function WorkingHoursSection(props: { uid: string; manage: boolean }) {
  const intl = useIntl();
  const t = (id: string, v?: Record<string, string | number>) => intl.formatMessage({ id }, v);
  const { locale } = useLocale();
  const authz = useAuthz();
  const queryClient = useQueryClient();
  const { dayName, dayNames } = useDayNames();
  const avail = usePractitionerAvailability(props.uid);
  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['availability', authz.clinicId, props.uid] });
  const [editing, setEditing] = useState<WorkingHoursView | null>(null);
  const [notice, setNotice] = useState<{ kind: 'ok' | 'warn' | 'err'; text: string } | null>(null);
  const removeRange = useMutation({
    mutationFn: deleteWorkingHours,
    onSuccess: invalidate,
    onError: () => setNotice({ kind: 'err', text: t('hours.removeFailed') as string }),
  });

  const hours = avail.data?.workingHours ?? [];

  if (avail.isError && isNoPractitioner(avail.error)) return <NoPractitionerNotice />;
  if (avail.isError)
    return (
      <Notice
        kind="err"
        role="alert"
        action={
          <Button variant="quiet" onClick={() => void avail.refetch()}>
            {t('table.retry')}
          </Button>
        }
      >
        {t('rec.sectionFailed')}
      </Notice>
    );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {props.manage && (
        <Presets
          uid={props.uid}
          existing={hours}
          onDone={(n) => {
            setNotice(n);
            invalidate();
          }}
        />
      )}

      {notice && (
        <Notice kind={notice.kind} role={notice.kind === 'err' ? 'alert' : undefined}>
          {notice.text}
        </Notice>
      )}

      <div className={a.sectionCard} style={{ inlineSize: '100%' }}>
        {avail.isPending && (
          <div className={a.row}>
            <Skeleton height="14px" width="50%" />
          </div>
        )}
        {!avail.isPending &&
          DAY_IDS.map((d) => {
            const ranges = hours.filter((h) => h.dayOfWeek === d);
            return (
              <div key={d} className={a.row}>
                <span className={a.rowKey}>{dayName(d)}</span>
                <span className={a.rowValue} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {ranges.length === 0 && (
                    <span style={{ color: 'var(--zc-faint)' }}>{t('hours.closed')}</span>
                  )}
                  {ranges.map((r) => (
                    <span
                      key={r.id}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    >
                      <Chip
                        kind={
                          modesOf(r).includes('off_site')
                            ? 'warn'
                            : modesOf(r).includes('online')
                              ? 'info'
                              : 'ok'
                        }
                      >
                        <span className="zc-tabular">
                          {localizeDigits(
                            `${minuteToHHMM(r.startMinute)}–${minuteToHHMM(r.endMinute)}`,
                            locale,
                          )}
                        </span>
                        {' · '}
                        {modesLabel(t, modesOf(r))}
                      </Chip>
                      {props.manage && (
                        <>
                          <Button variant="link" onClick={() => setEditing(r)}>
                            {t('hours.change')}
                          </Button>
                          <Button variant="link" onClick={() => removeRange.mutate(r.id)}>
                            {t('common.remove')}
                          </Button>
                        </>
                      )}
                    </span>
                  ))}
                </span>
              </div>
            );
          })}
      </div>

      {props.manage && editing && (
        <RangeForm
          key={editing.id}
          uid={props.uid}
          dayNames={dayNames}
          editing={editing}
          onDone={(n) => {
            setEditing(null);
            if (n) setNotice(n);
            invalidate();
          }}
          onCancel={() => setEditing(null)}
        />
      )}
      {props.manage && !editing && (
        <RangeForm
          uid={props.uid}
          dayNames={dayNames}
          onDone={(n) => {
            if (n) setNotice(n);
            invalidate();
          }}
        />
      )}
    </div>
  );
}

function NoPractitionerNotice() {
  const intl = useIntl();
  return (
    <Notice kind="info" title={intl.formatMessage({ id: 'member.noPractitionerTitle' })}>
      {intl.formatMessage({ id: 'member.noPractitionerBody' })}
    </Notice>
  );
}

/**
 * The six presets (in clinic × off site; all week / even / odd), fixed at
 * 08:00–20:00. Applying one REPLACES the schedule: the dialog names the days,
 * the hours, the place of work and how many current ranges go first. It runs
 * as the contract allows — one remove, then one add per day — and reports
 * exactly what happened; the week below is then ordinary, editable hours.
 */
function Presets(props: {
  uid: string;
  existing: readonly WorkingHoursView[];
  onDone: (n: { kind: 'ok' | 'warn' | 'err'; text: string }) => void;
}) {
  const intl = useIntl();
  const t = (id: string, v?: Record<string, string | number>) => intl.formatMessage({ id }, v);
  const { locale } = useLocale();
  const { dayName } = useDayNames();
  const [chosen, setChosen] = useState<{ pattern: PresetPattern; location: PresetLocation } | null>(
    null,
  );
  // ZOY-693: a preset row is a physical place; online is added on top.
  const [online, setOnline] = useState(false);
  const hoursText = localizeDigits(
    `${minuteToHHMM(PRESET_START_MINUTE)}–${minuteToHHMM(PRESET_END_MINUTE)}`,
    locale,
  );
  const whereText = (loc: PresetLocation, withOnline = false) =>
    modesLabel(t, presetModes(loc, withOnline));
  const daysText = (p: PresetPattern) => PRESET_DAYS[p].map(dayName).join(' · ');

  const apply = useMutation({
    mutationFn: async (pick: {
      pattern: PresetPattern;
      location: PresetLocation;
      online: boolean;
    }) => {
      const plan = planPreset(
        props.existing,
        pick.pattern,
        presetModes(pick.location, pick.online),
      );
      let removeFailed = 0;
      const addFailed: string[] = [];
      for (const id of plan.remove) {
        try {
          await deleteWorkingHours(id);
        } catch {
          removeFailed += 1;
        }
      }
      let added = 0;
      for (const r of plan.add) {
        try {
          await addWorkingHours(props.uid, r);
          added += 1;
        } catch (err) {
          addFailed.push(
            t('hours.presetAddFailed', {
              day: dayName(r.dayOfWeek),
              reason: errorText(
                err,
                t('common.failedKept') as string,
                t('hours.overlap') as string,
              ),
            }) as string,
          );
        }
      }
      return { added, removeFailed, addFailed, pick };
    },
    onSuccess: (r) => {
      setChosen(null);
      const problems = [
        ...(r.removeFailed > 0
          ? [t('hours.presetRemoveFailed', { n: r.removeFailed }) as string]
          : []),
        ...r.addFailed,
      ];
      props.onDone(
        problems.length === 0
          ? {
              kind: 'ok',
              text: t('hours.presetDone', {
                n: r.added,
                hours: hoursText,
                where: whereText(r.pick.location, r.pick.online),
              }) as string,
            }
          : {
              kind: 'warn',
              text: t('hours.presetPartial', { failed: problems.join('; ') }) as string,
            },
      );
    },
  });

  return (
    <div className={a.sectionCard} style={{ inlineSize: '100%' }}>
      <div style={{ padding: '14px 18px 6px' }}>
        <strong style={{ color: 'var(--zc-teal-deep)' }}>{t('hours.presetsTitle')}</strong>
        <p style={{ fontSize: 12.5, color: 'var(--zc-muted)', marginBlockStart: 2 }}>
          {t('hours.presetsSub')}
        </p>
      </div>
      {PRESET_LOCATIONS.map((loc) => (
        <div
          key={loc}
          className={a.row}
          style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}
        >
          <span className={a.rowKey}>{whereText(loc)}</span>
          <div
            className={a.cardsRow}
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}
          >
            {PRESET_PATTERNS.map((p) => (
              <button
                key={`${loc}-${p}`}
                type="button"
                className={a.chooserCard}
                onClick={() => {
                  setOnline(false);
                  setChosen({ pattern: p, location: loc });
                }}
                style={{ cursor: 'pointer', textAlign: 'start' }}
              >
                <strong style={{ color: 'var(--zc-teal-deep)' }}>{t(`hours.preset.${p}`)}</strong>
                <span style={{ fontSize: 12.5, color: 'var(--zc-ink-2)' }}>{daysText(p)}</span>
                <span style={{ fontSize: 12.5, color: 'var(--zc-muted)' }} className="zc-tabular">
                  {hoursText}
                </span>
              </button>
            ))}
          </div>
        </div>
      ))}
      <div className={a.row}>
        <span className={a.rowValue} style={{ fontSize: 12, color: 'var(--zc-muted)' }}>
          {t('hours.presetEditable')}
        </span>
      </div>

      <ConfirmDialog
        open={chosen !== null}
        title={
          chosen
            ? (t('hours.presetConfirmTitle', {
                name: `${t(`hours.preset.${chosen.pattern}`)} · ${whereText(chosen.location, online)}`,
              }) as string)
            : ''
        }
        body={
          chosen && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <span>
                {t('hours.presetConfirmBody', {
                  days: daysText(chosen.pattern),
                  hours: hoursText,
                  where: whereText(chosen.location, online),
                })}
              </span>
              <Toggle
                label={t('hours.presetOnline') as string}
                checked={online}
                onChange={setOnline}
                stateWord={(online ? t('common.enabled') : t('common.disabled')) as string}
              />
              <Notice kind={props.existing.length > 0 ? 'warn' : 'info'}>
                {props.existing.length > 0
                  ? t('hours.presetReplaces', { n: props.existing.length })
                  : t('hours.presetEmptyNow')}
              </Notice>
            </div>
          )
        }
        confirmLabel={t('hours.presetCta') as string}
        danger={props.existing.length > 0}
        busy={apply.isPending}
        onConfirm={() => chosen && apply.mutate({ ...chosen, online })}
        onClose={() => setChosen(null)}
      />
    </div>
  );
}

/**
 * Add a range, or CHANGE one (prefilled). A change is two contract writes in
 * the safe order (editPlan): a refused new range never leaves the person
 * without the old one — it is put back, and if even that fails the old range
 * is spelled out so it can be re-added by hand.
 */
function RangeForm(props: {
  uid: string;
  dayNames: string[];
  editing?: WorkingHoursView;
  onDone: (n: { kind: 'ok' | 'warn' | 'err'; text: string } | null) => void;
  onCancel?: () => void;
}) {
  const intl = useIntl();
  const t = (id: string, v?: Record<string, string | number>) => intl.formatMessage({ id }, v);
  const { locale } = useLocale();
  const e = props.editing;
  const [day, setDay] = useState(e ? String(e.dayOfWeek) : '6');
  const [from, setFrom] = useState(e ? minuteToHHMM(e.startMinute) : '09:00');
  const [to, setTo] = useState(e ? minuteToHHMM(e.endMinute) : '17:00');
  const [modes, setModes] = useState<DeliveryMode[]>(e ? modesOf(e) : ['in_clinic']);
  const [error, setError] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: async (next: RangeInput) => {
      if (!e) {
        await addWorkingHours(props.uid, next);
        return { kind: 'ok' as const, text: '' };
      }
      const plan = editPlan(e, next);
      if (plan === 'add-then-remove') {
        await addWorkingHours(props.uid, next); // refused → nothing changed
        await deleteWorkingHours(e.id);
        return { kind: 'ok' as const, text: t('hours.changed') as string };
      }
      await deleteWorkingHours(e.id);
      try {
        await addWorkingHours(props.uid, next);
        return { kind: 'ok' as const, text: t('hours.changed') as string };
      } catch (err) {
        const reason = errorText(
          err,
          t('common.failedKept') as string,
          t('hours.overlap') as string,
        );
        try {
          await addWorkingHours(props.uid, {
            dayOfWeek: e.dayOfWeek,
            startMinute: e.startMinute,
            endMinute: e.endMinute,
            modes: modesOf(e),
          });
          return { kind: 'warn' as const, text: t('hours.changeRefused', { reason }) as string };
        } catch {
          return {
            kind: 'err' as const,
            text: t('hours.changeRestoreFailed', {
              reason,
              old: `${props.dayNames[DAY_IDS.indexOf(e.dayOfWeek as (typeof DAY_IDS)[number])] ?? ''} ${localizeDigits(
                `${minuteToHHMM(e.startMinute)}–${minuteToHHMM(e.endMinute)}`,
                locale,
              )}`,
            }) as string,
          };
        }
      }
    },
    onSuccess: (n) => {
      setError(null);
      props.onDone(n.text ? n : null);
    },
    onError: (err) =>
      setError(errorText(err, t('common.failedKept') as string, t('hours.overlap') as string)),
  });

  // Time validity is a client concern (bad "From"/"To"): kept distinct from
  // API failures so a server error is never mislabelled as a time problem.
  const submit = () => {
    const s = hhmmToMinute(from);
    const en = hhmmToMinute(to);
    if (s === null || en === null || en <= s) {
      setError(t('hours.timeFormat') as string);
      return;
    }
    if (modes.length === 0) {
      setError(t('svc.modesNeeded') as string);
      return;
    }
    setError(null);
    run.mutate({ dayOfWeek: Number(day), startMinute: s, endMinute: en, modes });
  };

  return (
    <FormCard
      title={t(e ? 'hours.changeTitle' : 'hours.addRange') as string}
      onSubmit={submit}
      cta={t(e ? 'hours.changeCta' : 'common.add') as string}
      busy={run.isPending}
      secondary={
        props.onCancel
          ? { label: t('common.cancel') as string, onClick: props.onCancel }
          : undefined
      }
    >
      <FieldGrid>
        <SelectField
          label={t('hours.day') as string}
          value={day}
          onChange={setDay}
          options={DAY_IDS.map((d, i) => ({ value: String(d), label: props.dayNames[i] ?? '' }))}
        />
        <ModeSetField
          label={t('hours.where') as string}
          value={modes}
          onChange={setModes}
          help={t('hours.modesHelp') as string}
        />
        <TextField
          label={t('hours.from')}
          value={from}
          onChange={setFrom}
          help={t('hours.timeFormat')}
          machine
          error={error ?? undefined}
        />
        <TextField
          label={t('hours.to')}
          value={to}
          onChange={setTo}
          help={t('hours.timeFormat')}
          machine
        />
      </FieldGrid>
    </FormCard>
  );
}

/* --------------------------------- time off --------------------------------- */

export function TimeOffSection(props: { uid: string; manage: boolean }) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const { locale } = useLocale();
  const settings = useClinicSettings();
  const tz = settings?.timezone ?? 'Asia/Tehran';
  const cal = settings?.calendarSystem ?? 'jalali';
  const authz = useAuthz();
  const queryClient = useQueryClient();
  const avail = usePractitionerAvailability(props.uid);
  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['availability', authz.clinicId, props.uid] });
  const removeOff = useMutation({ mutationFn: deleteTimeOff, onSuccess: invalidate });
  const timeOff = avail.data?.timeOff ?? [];

  if (avail.isError && isNoPractitioner(avail.error)) return <NoPractitionerNotice />;
  if (avail.isError)
    return (
      <Notice
        kind="err"
        role="alert"
        action={
          <Button variant="quiet" onClick={() => void avail.refetch()}>
            {t('table.retry')}
          </Button>
        }
      >
        {t('rec.sectionFailed')}
      </Notice>
    );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className={a.sectionCard} style={{ inlineSize: '100%' }}>
        {avail.isPending && (
          <div className={a.row}>
            <Skeleton height="14px" width="50%" />
          </div>
        )}
        {!avail.isPending && timeOff.length === 0 && (
          <div className={a.row}>
            <span className={a.rowValue} style={{ color: 'var(--zc-faint)' }}>
              {t('hours.timeOffNone')}
            </span>
          </div>
        )}
        {timeOff.map((o) => (
          <div key={o.id} className={a.row}>
            <span className={a.rowValue}>
              {formatDate(o.startsAt, { locale, calendar: cal, timezone: tz })}{' '}
              <span className="zc-tabular">{formatTime(o.startsAt, { locale, timezone: tz })}</span>
              {' — '}
              {formatDate(o.endsAt, { locale, calendar: cal, timezone: tz })}{' '}
              <span className="zc-tabular">{formatTime(o.endsAt, { locale, timezone: tz })}</span>
              {o.reason ? ` · ${o.reason}` : ''}
            </span>
            {props.manage && (
              <span className={a.rowAction}>
                <Button variant="link" onClick={() => removeOff.mutate(o.id)}>
                  {t('common.remove')}
                </Button>
              </span>
            )}
          </div>
        ))}
      </div>

      {props.manage && <TimeOffForm uid={props.uid} onAdded={invalidate} />}
      {timeOff.length > 0 && <AffectedBookings uid={props.uid} timeOff={timeOff} />}
    </div>
  );
}

/**
 * ZOY-403: the typed date and time are a CLINIC-local wall clock — composed
 * with the clinic's timezone (never the browser's) and picked in the
 * clinic's calendar. ZOY-404: every refusal is named in text on its field
 * (aria-invalid comes with the field frame), and an API failure is reported
 * separately so a bad interval and a server error are never conflated.
 */
export function TimeOffForm(props: {
  uid: string;
  onAdded: () => void;
  /** ZOY-636: the same form records a ROOM blockout — a different writer and wording. */
  add?: (range: { startsAt: string; endsAt: string; reason?: string }) => Promise<unknown>;
  title?: string;
  cta?: string;
  note?: string;
}) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const { locale } = useLocale();
  const settings = useClinicSettings();
  const tz = settings?.timezone ?? 'Asia/Tehran';
  const cal = settings?.calendarSystem ?? 'jalali';
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [reason, setReason] = useState('');
  const [startTimeError, setStartTimeError] = useState<string | null>(null);
  const [endTimeError, setEndTimeError] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: (range: { startsAt: string; endsAt: string }) =>
      (props.add ?? ((r) => addTimeOff(props.uid, r)))({ ...range, reason: reason || undefined }),
    onMutate: () => setApiError(null),
    onSuccess: () => {
      setStartDate('');
      setStartTime('');
      setEndDate('');
      setEndTime('');
      setReason('');
      props.onAdded();
    },
    onError: (err) =>
      setApiError(
        err instanceof ApiError && err.serverMessage
          ? err.serverMessage
          : (t('common.failedKept') as string),
      ),
  });

  const submit = () => {
    setApiError(null);
    const sMin = hhmmToMinute(startTime);
    const eMin = hhmmToMinute(endTime);
    setStartTimeError(sMin === null ? (t('hours.timeFormat') as string) : null);
    setEndTimeError(eMin === null ? (t('hours.timeFormat') as string) : null);
    if (sMin === null || eMin === null) return;
    const sp = parseDateISO(startDate);
    const ep = parseDateISO(endDate);
    if (!sp || !ep) return; // the CTA is gated on complete dates
    const s = instantFromLocal(sp.y, sp.m, sp.d, sMin, tz);
    const e = instantFromLocal(ep.y, ep.m, ep.d, eMin, tz);
    if (e.getTime() <= s.getTime()) {
      setEndTimeError(t('hours.offOrder') as string);
      return;
    }
    add.mutate({ startsAt: s.toISOString(), endsAt: e.toISOString() });
  };

  return (
    <FormCard
      title={props.title ?? t('hours.recordTimeOff')}
      onSubmit={submit}
      cta={props.cta ?? t('hours.saveCta')}
      ctaDisabled={startDate === '' || startTime === '' || endDate === '' || endTime === ''}
      busy={add.isPending}
      footNote={props.note ?? t('hours.timeOffNote')}
    >
      {apiError && (
        <Notice kind="err" role="alert">
          {apiError}
        </Notice>
      )}
      <FieldGrid>
        <DateField
          label={t('hours.startsDate') as string}
          value={startDate}
          onChange={setStartDate}
          system={cal}
          locale={locale}
          yearsBack={1}
          yearsAhead={1}
        />
        <TextField
          label={t('hours.startsTime')}
          value={startTime}
          onChange={(v) => {
            setStartTime(v);
            setStartTimeError(null);
          }}
          help={t('hours.timeFormat')}
          error={startTimeError ?? undefined}
          machine
        />
        <DateField
          label={t('hours.endsDate') as string}
          value={endDate}
          onChange={setEndDate}
          system={cal}
          locale={locale}
          yearsBack={1}
          yearsAhead={1}
        />
        <TextField
          label={t('hours.endsTime')}
          value={endTime}
          onChange={(v) => {
            setEndTime(v);
            setEndTimeError(null);
          }}
          help={t('hours.timeFormat')}
          error={endTimeError ?? undefined}
          machine
        />
        <Span2>
          <TextField
            label={t('hours.reason')}
            value={reason}
            onChange={setReason}
            help={t('hours.reasonHelp')}
            requirement={{ word: t('signin.optional'), kind: 'optional' }}
          />
        </Span2>
      </FieldGrid>
    </FormCard>
  );
}

/** Booked sessions that fall inside each time-off entry — the real ZOY-250 window read. */
function AffectedBookings(props: {
  uid: string;
  timeOff: { id: string; startsAt: string; endsAt: string }[];
}) {
  const intl = useIntl();
  const { locale } = useLocale();
  const settings = useClinicSettings();
  const tz = settings?.timezone ?? 'Asia/Tehran';
  const cal = settings?.calendarSystem ?? 'jalali';
  const first = props.timeOff[0]!;
  const q = useBookingsWindow({
    from: new Date(first.startsAt),
    to: new Date(props.timeOff.reduce((m, o) => (o.endsAt > m ? o.endsAt : m), first.endsAt)),
    practitionerUid: props.uid,
    status: ['booked'],
  });
  const count = (q.data ?? []).filter((b) =>
    props.timeOff.some((o) => b.startsAt < o.endsAt && b.endsAt > o.startsAt),
  ).length;
  if (q.isPending) return null;
  return (
    <Notice
      kind={count > 0 ? 'warn' : 'info'}
      action={
        count > 0 ? (
          <Link
            to="/calendar"
            search={{
              pr: props.uid,
              date: localDateISO(new Date(first.startsAt), tz),
              view: 'week',
            }}
          >
            {intl.formatMessage({ id: 'md.openCalendar' })}
          </Link>
        ) : undefined
      }
    >
      {intl.formatMessage(
        { id: 'hours.affected' },
        { n: count, date: formatDate(first.startsAt, { locale, calendar: cal, timezone: tz }) },
      )}
    </Notice>
  );
}
